import { Tab } from '@hologram-dimension/tab';
import { BorderlessPanel, CustomPostpaidDisclaimer } from 'components';
import { FC } from 'react';
import { SWRConfig, SWRConfiguration } from 'swr';
import sharedPageStyles from 'pages/sharedPage.module.css';
import clsx from 'clsx';
import PlanDetailsHeader from 'components/PlanDetails/PlanDetailsHeader/PlanDetailsHeader';
import PlanPricingPanel from 'components/PlanDetails/PlanPricingPanel/PlanPricingPanel';
import PlanCoveragePanel from 'components/PlanDetails/PlanCoveragePanel/PlanCoveragePanel';
import useOrganization from 'state/contexts/Organization/useOrganization';
import usePlans from 'api/legacy/organization/usePlans';
import useOrganizationDevicesPlans from 'api/legacy/organization/useOrganizationDevicesPlans';
import { Loader } from '@hologram-dimension/loader';
import { PanelNotification } from '@hologram-dimension/panel-notification';
import usePools from 'api/legacy/organization/usePools';
import usePool from 'api/legacy/plans/usePool';
import useBalance from 'api/legacy/organization/useBalance';
import styles from './Plan.module.css';

// TODO: Move to a shared config
const simInventorySwrConfig: SWRConfiguration = {
  dedupingInterval: 60000,
  focusThrottleInterval: 300000,
  loadingTimeout: 3000,
  errorRetryInterval: 10000,
  errorRetryCount: 3,
};

const views = {
  pricing: 'Pricing',
  coverage: 'Coverage',
};

export type PlanViews = keyof typeof views | null;

interface PlanPageProps {
  planId: PlanId;
  zone: string;
  viewKey: PlanViews;
  handleTabClick: (view: PlanViews) => void;
  simInventoryBaseUrl: string;
  planCoverageUrl: string;
}

const PlanPageInner: FC<PlanPageProps> = ({
  planId,
  zone,
  viewKey,
  handleTabClick,
  simInventoryBaseUrl,
  planCoverageUrl,
}) => {
  const organizationId = useOrganization();
  const { balance, isLoading: isBalanceLoading, error: balanceError } = useBalance(organizationId);

  const {
    plans,
    isLoading: isPlansLoading,
    error: plansError,
  } = usePlans({ organizationId, includezdr: true, allZones: true });

  const {
    plans: devicesPlans,
    isLoading: isDevicesPlansLoading,
    error: devicesPlansError,
  } = useOrganizationDevicesPlans(organizationId);
  const { pools, isLoading: isPoolsLoading, error: poolsError } = usePools({ organizationId });

  const plan = plans.find((p) => p.id === planId && p.zone === zone);
  const simCount =
    devicesPlans.find(
      (devicePlan) => devicePlan?.planid === plan?.id && devicePlan?.zone === plan?.zone
    )?.count ?? 0;
  const matchingPool = pools.find((p) => p.dataplansubscriptionid === planId);

  const { pool, isLoading: isPoolLoading, error: poolError } = usePool(matchingPool?.poolplanid);
  const error = plansError || devicesPlansError || poolsError || poolError || balanceError;
  const isLoading =
    isPlansLoading || isDevicesPlansLoading || isPoolsLoading || isPoolLoading || isBalanceLoading;

  if (error) {
    return (
      <PanelNotification variant="error">
        There was a problem loading this data plan.
      </PanelNotification>
    );
  }

  if (isLoading) {
    return <Loader>Loading plan details...</Loader>;
  }
  if (!plan) {
    return <PanelNotification>No matching data plan found.</PanelNotification>;
  }

  return (
    <div className={clsx(sharedPageStyles.pageWrapper, styles.wrapper)}>
      <BorderlessPanel
        headerContents={
          <PlanDetailsHeader
            plan={plan}
            simInventoryBaseUrl={simInventoryBaseUrl}
            staticPool={pool}
            simCount={simCount}
          />
        }
        headerClassName={styles.header}
        contentsClassName={sharedPageStyles.viewsWrapper}
        tabs={
          <div className={clsx(sharedPageStyles.tabWrapper, styles.tabWrapper)}>
            {Object.entries(views).map(([view, viewName]) => (
              <Tab
                key={view}
                id={`plan-tab-${view}`}
                selected={view === viewKey}
                onClick={() => handleTabClick(view as PlanViews)}
              >
                {viewName}
              </Tab>
            ))}
          </div>
        }
      >
        <div className={styles.contents}>
          {balance?.billing_version === 3 && <CustomPostpaidDisclaimer />}
          {viewKey === 'pricing' && (
            <PlanPricingPanel
              plan={plan}
              planCoverageUrl={planCoverageUrl}
              staticPool={pool}
              simCount={simCount}
            />
          )}
          {viewKey === 'coverage' && <PlanCoveragePanel planId={planId} zone={zone} />}
        </div>
      </BorderlessPanel>
    </div>
  );
};

const PlanPage: FC<PlanPageProps> = ({
  planId,
  zone,
  viewKey,
  handleTabClick,
  simInventoryBaseUrl,
  planCoverageUrl,
}) => (
  <SWRConfig value={simInventorySwrConfig}>
    <PlanPageInner
      planId={planId}
      zone={zone}
      viewKey={viewKey}
      handleTabClick={handleTabClick}
      simInventoryBaseUrl={simInventoryBaseUrl}
      planCoverageUrl={planCoverageUrl}
    />
  </SWRConfig>
);

export default PlanPage;
